import React, { useEffect, useState } from 'react';
import {
	Dialog,
	DialogContent,
	Box,
	Typography,
	Avatar,
	Chip,
	IconButton,
	Divider,
	CircularProgress,
} from '@mui/material';
import {
	Close as CloseIcon,
	Phone as PhoneIcon,
	WorkOutline as WorkOutlineIcon,
	Layers as LayersIcon,
	LocationOnOutlined as LocationIcon,
	Public as GlobeIcon,
	MailOutline as MailIcon,
} from '@mui/icons-material';
import { headingBlue } from '../../assets/colors';
import { fetchWORKerDetails } from '../../backend/backendApis';
import HttpService from '../../backend/requestApis';

const AVATAR_COLORS = [
	'#2563eb', // Royal Blue
	'#7c3aed', // Purple
	'#db2777', // Pink
	'#059669', // Emerald
	'#d97706', // Amber Gold
	'#0891b2', // Teal
	'#4f46e5', // Indigo
	'#c026d3', // Fuchsia
	'#e11d48', // Crimson
	'#0284c7', // Sky Blue
];

const getInitials = (name) => {
	if (!name || typeof name !== 'string') return 'M';
	const cleanName = name.trim();
	if (!cleanName) return 'M';
	const parts = cleanName.split(/\s+/);
	if (parts.length >= 2) {
		return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
	}
	return cleanName[0].toUpperCase();
};

const getAvatarColor = (nameOrContact) => {
	if (!nameOrContact) return AVATAR_COLORS[0];
	let hash = 0;
	for (let i = 0; i < nameOrContact.length; i++) {
		hash = nameOrContact.charCodeAt(i) + ((hash << 5) - hash);
	}
	const index = Math.abs(hash) % AVATAR_COLORS.length;
	return AVATAR_COLORS[index];
};

const formatWORKerCategory = (cat) => {
	if (!cat) return 'N/A';
	const lower = String(cat).toLowerCase().trim();
	if (lower === 'committed') return 'Committed WORKers';
	if (lower === 'noncommitted' || lower === 'uncommitted') return 'Supported WORKer';
	return String(cat)
		.replace(/([A-Z])/g, ' $1')
		.replace(/[_-]/g, ' ')
		.trim()
		.replace(/\b\w/g, (c) => c.toUpperCase());
};

const formatWORKerType = (type) => {
	if (!type || type === 'false' || type === false) return 'N/A';
	const lower = String(type).toLowerCase().trim();
	if (lower === 'virtuallyactive') return 'Virtually Active';
	if (lower === 'inactive') return 'Inactive';
	return String(type)
		.replace(/([A-Z])/g, ' $1')
		.replace(/[_-]/g, ' ')
		.trim()
		.replace(/\b\w/g, (c) => c.toUpperCase());
};

const getUserPayload = () => {
	const token = localStorage.getItem('token');
	if (!token) return {};
	try {
		const parts = token.split('.');
		if (parts.length < 2) return {};
		const base64Url = parts[1];
		const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
		const jsonPayload = decodeURIComponent(
			atob(base64)
				.split('')
				.map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
				.join('')
		);
		return JSON.parse(jsonPayload);
	} catch (e) {
		return {};
	}
};

const WORKerProfileDialog = ({ open, onClose }) => {
	const [memberDetails, setMemberDetails] = useState(null);
	const [isLoading, setIsLoading] = useState(false);

	const fetchDetails = async () => {
		const payload = getUserPayload();
		const contactNum =
			localStorage.getItem('contact') ||
			payload?.contact ||
			payload?.contactNumber;

		if (!contactNum) return;

		setIsLoading(true);
		try {
			const options = {
				url: fetchWORKerDetails,
				method: 'GET',
				params: {
					contactNumber: contactNum,
				},
				headers: {
					Authorization: localStorage.getItem('token')
						? `Bearer ${localStorage.getItem('token')}`
						: '',
				},
			};

			const response = await HttpService(options);
			if (response?.data?.data) {
				setMemberDetails(response.data.data);
				try {
					localStorage.setItem(
						'@cached_member_details',
						JSON.stringify(response.data.data)
					);
				} catch (e) {}
			}
		} catch (err) {
			console.log('Error fetching WORKer profile details:', err);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		if (open) {
			// Populate from cache first if available
			try {
				const cached = localStorage.getItem('@cached_member_details');
				if (cached) {
					setMemberDetails(JSON.parse(cached));
				}
			} catch (e) {}
			fetchDetails();
		}
	}, [open]);

	const payload = getUserPayload();
	const name =
		memberDetails?.name ||
		payload?.name ||
		localStorage.getItem('name') ||
		'Member';
	const contact =
		memberDetails?.contactNumber ||
		memberDetails?.contact ||
		payload?.contact ||
		payload?.contactNumber ||
		localStorage.getItem('contact') ||
		'N/A';
	const district =
		memberDetails?.district ||
		memberDetails?.districtName ||
		memberDetails?.assignedDistrict ||
		payload?.district ||
		payload?.districtName ||
		localStorage.getItem('district') ||
		'N/A';
	const state =
		memberDetails?.state ||
		memberDetails?.stateName ||
		memberDetails?.chapter ||
		memberDetails?.chapterName ||
		payload?.state ||
		payload?.chapter ||
		localStorage.getItem('state') ||
		'N/A';
	const designation =
		memberDetails?.designation ||
		payload?.designation ||
		payload?.role ||
		localStorage.getItem('designation') ||
		'Member';
	const email = memberDetails?.email || payload?.email || 'N/A';

	const rawCategory =
		memberDetails?.WORKerCateogary ||
		memberDetails?.WORKerCategory ||
		memberDetails?.workerCategory ||
		memberDetails?.workerCateogary ||
		payload?.WORKerCateogary ||
		payload?.WORKerCategory;

	const workerCategory = formatWORKerCategory(rawCategory);

	const rawType =
		memberDetails?.WORKerType ||
		memberDetails?.workerType ||
		payload?.WORKerType ||
		payload?.workerType;

	const workerType = formatWORKerType(rawType);

	const displayDesignation =
		designation.charAt(0).toUpperCase() + designation.slice(1);
	const initials = getInitials(name);
	const avatarColor = getAvatarColor(name || contact);

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="xs"
			fullWidth
			PaperProps={{
				sx: {
					borderRadius: '16px',
					boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
					overflow: 'hidden',
					position: 'relative',
					backgroundColor: '#ffffff',
				},
			}}
		>
			{/* Close Icon Button */}
			<IconButton
				onClick={onClose}
				size="small"
				sx={{
					position: 'absolute',
					top: 12,
					right: 12,
					color: '#64748b',
					zIndex: 2,
					backgroundColor: 'rgba(255,255,255,0.85)',
					'&:hover': {
						backgroundColor: '#f1f5f9',
						color: '#0f172a',
					},
				}}
			>
				<CloseIcon fontSize="small" />
			</IconButton>

			<DialogContent sx={{ p: 0 }}>
				{/* Top Header Card */}
				<Box
					sx={{
						pt: 3.5,
						pb: 2.5,
						px: 3,
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
						borderBottom: '1px solid #f1f5f9',
					}}
				>
					<Avatar
						sx={{
							width: 72,
							height: 72,
							backgroundColor: avatarColor,
							fontSize: '1.75rem',
							fontWeight: 700,
							color: '#ffffff',
							boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
							mb: 1.5,
						}}
					>
						{initials}
					</Avatar>

					<Typography
						sx={{
							fontSize: '1.2rem',
							fontWeight: 700,
							color: headingBlue,
							textAlign: 'center',
							lineHeight: 1.3,
							mb: 0.8,
						}}
					>
						{name}
					</Typography>

					<Chip
						label={displayDesignation}
						size="small"
						sx={{
							backgroundColor: '#e8f0fe',
							color: '#1e40af',
							fontWeight: 600,
							fontSize: '0.78rem',
							height: 24,
							borderRadius: '12px',
						}}
					/>
				</Box>

				{/* Member Details Info List */}
				<Box sx={{ px: 2.5, py: 2 }}>
					{/* Contact Number */}
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							py: 1,
							px: 1,
						}}
					>
						<Box
							sx={{
								width: 36,
								height: 36,
								borderRadius: '8px',
								backgroundColor: '#eef4fa',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								mr: 1.8,
								flexShrink: 0,
							}}
						>
							<PhoneIcon sx={{ fontSize: 18, color: headingBlue }} />
						</Box>
						<Box sx={{ flexGrow: 1 }}>
							<Typography
								sx={{
									fontSize: '0.72rem',
									color: '#64748b',
									fontWeight: 500,
									textTransform: 'uppercase',
									letterSpacing: '0.5px',
								}}
							>
								Contact Number
							</Typography>
							<Typography
								sx={{
									fontSize: '0.9rem',
									fontWeight: 600,
									color: '#1e293b',
								}}
							>
								{contact}
							</Typography>
						</Box>
					</Box>
					<Divider sx={{ my: 0.5, borderColor: '#f1f5f9' }} />

					{/* WORKer Category */}
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							py: 1,
							px: 1,
						}}
					>
						<Box
							sx={{
								width: 36,
								height: 36,
								borderRadius: '8px',
								backgroundColor: '#eef4fa',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								mr: 1.8,
								flexShrink: 0,
							}}
						>
							<WorkOutlineIcon sx={{ fontSize: 18, color: headingBlue }} />
						</Box>
						<Box sx={{ flexGrow: 1 }}>
							<Typography
								sx={{
									fontSize: '0.72rem',
									color: '#64748b',
									fontWeight: 500,
									textTransform: 'uppercase',
									letterSpacing: '0.5px',
								}}
							>
								WORKer Category
							</Typography>
							<Typography
								sx={{
									fontSize: '0.9rem',
									fontWeight: 600,
									color: '#1e293b',
								}}
							>
								{workerCategory}
							</Typography>
						</Box>
					</Box>
					<Divider sx={{ my: 0.5, borderColor: '#f1f5f9' }} />

					{/* WORKer Type */}
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							py: 1,
							px: 1,
						}}
					>
						<Box
							sx={{
								width: 36,
								height: 36,
								borderRadius: '8px',
								backgroundColor: '#eef4fa',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								mr: 1.8,
								flexShrink: 0,
							}}
						>
							<LayersIcon sx={{ fontSize: 18, color: headingBlue }} />
						</Box>
						<Box sx={{ flexGrow: 1 }}>
							<Typography
								sx={{
									fontSize: '0.72rem',
									color: '#64748b',
									fontWeight: 500,
									textTransform: 'uppercase',
									letterSpacing: '0.5px',
								}}
							>
								WORKer Type
							</Typography>
							<Typography
								sx={{
									fontSize: '0.9rem',
									fontWeight: 600,
									color: '#1e293b',
								}}
							>
								{workerType}
							</Typography>
						</Box>
					</Box>
					<Divider sx={{ my: 0.5, borderColor: '#f1f5f9' }} />

					{/* District */}
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							py: 1,
							px: 1,
						}}
					>
						<Box
							sx={{
								width: 36,
								height: 36,
								borderRadius: '8px',
								backgroundColor: '#eef4fa',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								mr: 1.8,
								flexShrink: 0,
							}}
						>
							<LocationIcon sx={{ fontSize: 18, color: headingBlue }} />
						</Box>
						<Box sx={{ flexGrow: 1 }}>
							<Typography
								sx={{
									fontSize: '0.72rem',
									color: '#64748b',
									fontWeight: 500,
									textTransform: 'uppercase',
									letterSpacing: '0.5px',
								}}
							>
								District
							</Typography>
							<Typography
								sx={{
									fontSize: '0.9rem',
									fontWeight: 600,
									color: '#1e293b',
								}}
							>
								{district}
							</Typography>
						</Box>
					</Box>
					<Divider sx={{ my: 0.5, borderColor: '#f1f5f9' }} />

					{/* State */}
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							py: 1,
							px: 1,
						}}
					>
						<Box
							sx={{
								width: 36,
								height: 36,
								borderRadius: '8px',
								backgroundColor: '#eef4fa',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								mr: 1.8,
								flexShrink: 0,
							}}
						>
							<GlobeIcon sx={{ fontSize: 18, color: headingBlue }} />
						</Box>
						<Box sx={{ flexGrow: 1 }}>
							<Typography
								sx={{
									fontSize: '0.72rem',
									color: '#64748b',
									fontWeight: 500,
									textTransform: 'uppercase',
									letterSpacing: '0.5px',
								}}
							>
								State
							</Typography>
							<Typography
								sx={{
									fontSize: '0.9rem',
									fontWeight: 600,
									color: '#1e293b',
								}}
							>
								{state}
							</Typography>
						</Box>
					</Box>

					{/* Email (only if available) */}
					{email && email !== 'N/A' && (
						<>
							<Divider sx={{ my: 0.5, borderColor: '#f1f5f9' }} />
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									py: 1,
									px: 1,
								}}
							>
								<Box
									sx={{
										width: 36,
										height: 36,
										borderRadius: '8px',
										backgroundColor: '#eef4fa',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										mr: 1.8,
										flexShrink: 0,
									}}
								>
									<MailIcon sx={{ fontSize: 18, color: headingBlue }} />
								</Box>
								<Box sx={{ flexGrow: 1 }}>
									<Typography
										sx={{
											fontSize: '0.72rem',
											color: '#64748b',
											fontWeight: 500,
											textTransform: 'uppercase',
											letterSpacing: '0.5px',
										}}
									>
										Email
									</Typography>
									<Typography
										sx={{
											fontSize: '0.9rem',
											fontWeight: 600,
											color: '#1e293b',
										}}
									>
										{email}
									</Typography>
								</Box>
							</Box>
						</>
					)}
				</Box>
			</DialogContent>
		</Dialog>
	);
};

export default WORKerProfileDialog;
